/**
 * Match environment — applying the TIME OF DAY / weather-look selection to the
 * render stack.
 *
 * WEATHER's PHYSICS half needs none of this: 'rain' sets `MatchConfig.wetPitch`,
 * which the sim reads for real physics (§3.2) and vfx.ts reads off the same
 * config in onPitchConfig to enable the rain field + wet sheen. One flag, both
 * honest. `rainLevel` below is the cosmetic-only half (see its own doc) and
 * changes nothing about that.
 *
 * TIME OF DAY is presentation-only and lives in the lighting rig:
 * render/lighting.ts exports `LightingRig.setSky(preset, ctx)`. The catch: the
 * Renderer shell keeps its subsystem list and RenderContext in PRIVATE fields,
 * its public API is frozen (renderer.ts header), and render/** is not this
 * module's to edit.
 *
 * TODO(orchestrator): expose lighting rig — give Renderer a public
 * `setSky(preset: SkyPreset): void` (one line delegating to the rig) and
 * replace `applySkyPreset`'s guarded reach below with that call.
 *
 * Until then `applySkyPreset` reaches through the shell AT RUNTIME (TS
 * `private` is compile-time only) with every step type-guarded, so if the
 * shell's internals are ever renamed the reach fails SOFT: it returns false,
 * the pitch stays at the rig's default 'day', and nothing crashes. The
 * selection itself is persisted in the settings blob and mirrored in
 * MATCH_ENVIRONMENT, so once the public seam exists the stored choice applies
 * with no migration.
 *
 * ── `applyTimeOfDay` vs `applySkyPreset` (stadium-presentation pass) ────────
 * `render/lighting.ts`'s `SkyPreset` now carries THREE MORE looks than
 * `ui/settings.ts`'s `TimeOfDay` does — `cloudy`, `rain`, `heavyRain` — added
 * for the owner's "rainy, heavy rain, cloudy, overcast" ask. `ui/settings.ts`
 * and `ui/menu.ts` (the setup-screen dropdown) are OUTSIDE this task's given
 * scope, so `TimeOfDay` itself is untouched and `applyTimeOfDay` keeps its
 * exact old signature/behaviour for every existing caller (practice.ts).
 * `applySkyPreset` is the SAME reach widened to the full `SkyPreset` union —
 * `applyTimeOfDay` is now just `applySkyPreset` called with a `TimeOfDay`
 * (a real subtype of `SkyPreset`, so this is a safe upcast, not a cast).
 * Call `applySkyPreset` directly to reach the three new looks today; wiring
 * them into the setup screen is the one remaining step, left to whoever owns
 * `ui/settings.ts`/`ui/menu.ts`.
 */

import type { Renderer } from '../render/renderer.js';
import type { SkyPreset } from '../render/lighting.js';
import type { TimeOfDay, Weather } from './settings.js';

/**
 * Module-level mirror of the active selection — the wiring point the
 * orchestrator can read from anywhere once the renderer seam exists.
 *
 * `rainLevel` is a cosmetic-only 0..2 dial (0 dry, 1 "rain", 2 "heavy rain"),
 * held here as the SAME kind of forward-wired mirror `weather` already is in
 * this file (persisted/settable today, with no live reader yet — see the
 * class doc above on `weather`/`applyTimeOfDay` predating a public renderer
 * seam). It is deliberately kept SEPARATE from `MatchConfig.wetPitch`, the
 * boolean the sim's real friction/restitution physics reads (§3.2): a
 * cosmetic dial in a `ui/` module has no path into `/sim` and must not grow
 * one (D1/D2 sim purity). render/lighting.ts's `rain`/`heavyRain` `SkyPreset`
 * pair already reads distinctly on screen (sky/fog/floods) without this
 * field; `rainLevel` is here for whichever render subsystem later wants a
 * matching wet-pitch INTENSITY (as opposed to today's on/off `uWet`), so that
 * knob has one obvious home instead of a second ad hoc one.
 */
export const MATCH_ENVIRONMENT: { timeOfDay: TimeOfDay; weather: Weather; rainLevel: 0 | 1 | 2 } = {
  timeOfDay: 'day',
  weather: 'clear',
  rainLevel: 0,
};

interface SkyCapable {
  name?: unknown;
  setSky?: unknown;
}

/**
 * Apply ANY sky/weather-look preset (the full `SkyPreset` union — see header)
 * to a live renderer. Returns true if the rig accepted it, false if the
 * guarded reach found nothing (see header — soft failure only).
 */
export function applySkyPreset(renderer: Renderer, preset: SkyPreset): boolean {
  const shell = renderer as unknown as { subsystems?: unknown; ctx?: unknown };
  const list = shell.subsystems;
  const ctx = shell.ctx;
  if (!Array.isArray(list) || typeof ctx !== 'object' || ctx === null) return false;

  for (const sub of list as SkyCapable[]) {
    if (
      sub !== null &&
      typeof sub === 'object' &&
      sub.name === 'lighting' &&
      typeof sub.setSky === 'function'
    ) {
      try {
        (sub.setSky as (preset: SkyPreset, ctx: unknown) => void).call(sub, preset, ctx);
        return true;
      } catch (err) {
        console.warn('[fg] setSky failed — staying on the default sky:', err);
        return false;
      }
    }
  }
  return false;
}

/**
 * The four-preset `TimeOfDay` path every existing caller (practice.ts) uses —
 * unchanged behaviour, now implemented on top of `applySkyPreset` (see header
 * for why the two exist side by side).
 */
export function applyTimeOfDay(renderer: Renderer, timeOfDay: TimeOfDay): boolean {
  MATCH_ENVIRONMENT.timeOfDay = timeOfDay;
  return applySkyPreset(renderer, timeOfDay);
}
